import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, ModalBody } from "reactstrap";
import { MdUndo } from "react-icons/md";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetChannels, useDeleteChannel, useUpdateChannel } from "api/channels/service";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTranslation } from "react-i18next";
import { getChannelTypeOptions } from "../../../constants/channelTypes";
import classNames from "classnames";
import ChannelsFilters from "./components/ChannelsFilters";
import ChannelsCards from "./components/ChannelsCards";

const Channels = ({ activeColor = "secondary", account, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState([]);
  const [statusFilter, setStatusFilter] = useState([{ value: "active", label: t("channels.active") }]);

  const searchTypeArray = useMemo(
    () => searchType.map((option) => option.value),
    [searchType]
  );
  const statusFilterValues = useMemo(
    () => statusFilter.map((option) => option.value),
    [statusFilter]
  );

  const { data: channelsData, isLoading } = useGetChannels(
    searchTypeArray,
    page,
    pageSize,
    search,
    statusFilterValues.length > 0 ? statusFilterValues : ["active"]
  );

  const isTheme2 = shellThemeVariant === "2";
  const ChannelsTable = isTheme2 ? ReactTable2 : ReactTable;
  const deleteChannelMutation = useDeleteChannel();
  const updateChannelMutation = useUpdateChannel();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  const typeOptions = useMemo(() => getChannelTypeOptions(t), [t]);

  const handleDeleteClick = (channelId) => {
    setSelectedAction({ type: "delete", channelId });
    setDeleteModalOpen(true);
  };

  const handleRestoreClick = (channelId) => {
    setSelectedAction({ type: "restore", channelId });
    setDeleteModalOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedAction?.channelId) {
      return;
    }

    if (selectedAction.type === "restore") {
      updateChannelMutation.mutate(
        {
          id: selectedAction.channelId,
          data: { is_active: true, channel_status: "active" },
        },
        {
          onSuccess: () => {
            setDeleteModalOpen(false);
            setSelectedAction(null);
          },
        }
      );
      return;
    }

    deleteChannelMutation.mutate(selectedAction.channelId, {
      onSuccess: () => {
        setDeleteModalOpen(false);
        setSelectedAction(null);
      },
    });
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedAction(null);
  };

  const columns = useMemo(
    () => [
      { Header: t("id"), accessor: "channel_id" },
      { Header: t("name"), accessor: "name" },
      { Header: t("description"), accessor: "description" },
      { Header: t("type"), accessor: "type" },
      { Header: t("status"), accessor: "status" },
      {
        Header: "",
        id: "actions",
        Cell: ({ row }) => {
          const channel = row.original;
          return (
            <div className="actions-right" style={{ textAlign: "right" }}>
              {account.permissions.channels_edit && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/channels/${channel.channel_id}`);
                  }}
                >
                  <i className="tim-icons icon-pencil" />
                </Button>
              )}
              {account.permissions.channels_delete && (
                channel.is_active === false ? (
                  <Button
                    color="link"
                    size="sm"
                    className="btn-icon btn-link like table-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestoreClick(channel.channel_id);
                    }}
                    disabled={updateChannelMutation.isPending}
                    title={t("channels.restoreChannel")}
                  >
                    {updateChannelMutation.isPending &&
                    selectedAction?.type === "restore" &&
                    selectedAction?.channelId === channel.channel_id ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <MdUndo size={16} />
                    )}
                  </Button>
                ) : (
                  <Button
                    color="link"
                    size="sm"
                    className="btn-icon btn-link like table-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(channel.channel_id);
                    }}
                    disabled={deleteChannelMutation.isPending}
                    title={t("channels.deleteChannel")}
                  >
                    {deleteChannelMutation.isPending &&
                    selectedAction?.type === "delete" &&
                    selectedAction?.channelId === channel.channel_id ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <i className="tim-icons icon-simple-remove" />
                    )}
                  </Button>
                )
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      account.permissions.channels_delete,
      account.permissions.channels_edit,
      deleteChannelMutation.isPending,
      updateChannelMutation.isPending,
      navigate,
      selectedAction,
      t,
    ]
  );

  const rows = useMemo(() => {
    const list = channelsData?.results || [];
    return list.map((channel) => ({
      channel_id: channel.channel_id,
      documentId: channel.documentId,
      name: channel.legal_name,
      description: channel.name,
      is_active: channel.is_active,
      type: channel.type
        ? t(`channels.types.${channel.type}`, { defaultValue: channel.type })
        : "",
      status: channel.channel_status
        ? channel.channel_status.charAt(0).toUpperCase() + channel.channel_status.slice(1)
        : "",
    }));
  }, [channelsData, t]);

  const headerActions = account.permissions.channels_add ? (
    <Button
      size="sm"
      style={{ ...activeColorStyle, color: "white" }}
      onClick={() => navigate("/channels/add")}
    >
      {t("channels.addChannel")}
    </Button>
  ) : null;

  const filters = (
    <ChannelsFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      search={search}
      onSearchChange={setSearch}
      searchType={searchType}
      onSearchTypeChange={setSearchType}
      statusFilter={statusFilter}
      onStatusFilterChange={(value) => {
        setStatusFilter(value);
        setPage(1);
      }}
      typeOptions={typeOptions}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: isTheme2 ? "13px" : undefined, minWidth: 0, maxWidth: "100%" }}
    >
      <ChannelsTable
        activeColor={activeColor}
        data={rows}
        columns={columns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
        manualPagination={true}
        pageCount={channelsData?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={channelsData?.pagination?.total}
        pageSize={pageSize}
        noDataText={t("channels.noChannelsFound")}
        loading={isLoading}
        loadingText={t("channels.loading")}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onRowClick={(row) => navigate(`/channels/${row.channel_id}`)}
      />
    </div>
  );


  return (
    <div
      className={classNames("content", "search-channels", {
        "search-channels--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-channels-inset": isTheme2,
      })}
    >
      <ChannelsCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
        headerActions={headerActions}
      />

      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">
            {selectedAction?.type === "restore"
              ? t("channels.restoreChannel")
              : t("channels.deleteChannel")}
          </h6>
        </div>
        <ModalBody className="text-center">
          <p>
            {selectedAction?.type === "restore"
              ? t("channels.restoreConfirmation")
              : t("channels.deleteConfirmation")}
          </p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteChannelMutation.isPending}
          >
            {t("channels.cancel")}
          </Button>
          <Button
            style={{
              background: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmAction}
            disabled={deleteChannelMutation.isPending || updateChannelMutation.isPending}
          >
            {deleteChannelMutation.isPending || updateChannelMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                />
                {selectedAction?.type === "restore"
                  ? t("channels.restoring")
                  : t("channels.deleting")}
              </>
            ) : (
              selectedAction?.type === "restore"
                ? t("channels.yesRestore")
                : t("channels.yesDelete")
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Channels;
